import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/site/Reveal";
import { NAV_SECTIONS } from "../lib/navigation";

const HERO_IMAGE = "/images/hero.jpg";
const FOUNDER_IMAGE = "/images/founder.jpg";

const Hero = () => (
  <section
    className="relative h-screen min-h-[680px] w-full overflow-hidden"
    data-testid="home-hero"
  >
    <img
      src={HERO_IMAGE}
      alt="Habitat contemporain en harmonie avec son paysage"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/20 to-black/55" />
    <div className="absolute inset-0 bg-gradient-to-tr from-black/55 via-black/15 to-transparent" />
    <div className="absolute inset-0 flex items-end">
      <div className="container-rozier pb-20 md:pb-28 w-full">
        <div className="max-w-3xl">
          <p
            className="text-white/80 uppercase tracking-[0.32em] text-[11px] mb-6 opacity-0 animate-fade-in"
            style={{ animationDelay: "200ms" }}
          >
            Bâti · Paysage · Énergétique
          </p>
          <h1
            className="font-serif text-white text-[44px] md:text-[68px] lg:text-[88px] leading-[1.02] tracking-tight font-light opacity-0 animate-fade-up"
            style={{ animationDelay: "400ms" }}
          >
            L'invisible structure
            <br />
            <span className="italic gold-text-shimmer">le visible.</span>
          </h1>
          <p
            className="text-white/85 text-base md:text-lg mt-8 max-w-xl leading-relaxed font-light opacity-0 animate-fade-up"
            style={{ animationDelay: "700ms" }}
          >
            Rozier Concept conçoit des espaces durables, esthétiques et profondément
            agréables à vivre. Une lecture sensible du lieu, du paysage à la charpente.
          </p>
          <div
            className="flex flex-wrap gap-4 mt-10 opacity-0 animate-fade-up"
            style={{ animationDelay: "950ms" }}
          >
            <Link
              to="/la-methode-rozier"
              className="inline-flex items-center gap-3 bg-white text-charcoal px-8 py-4 uppercase tracking-[0.28em] text-xs hover:bg-gold hover:text-white transition-colors duration-300"
              data-testid="hero-cta-methode"
            >
              La Méthode Rozier
              <ArrowUpRight size={14} />
            </Link>
            <a
              href="#metiers"
              className="inline-flex items-center gap-3 border border-white/60 text-white px-8 py-4 uppercase tracking-[0.28em] text-xs hover:border-gold hover:text-gold transition-colors duration-300"
              data-testid="hero-cta-metiers"
            >
              Nos trois métiers
            </a>
          </div>
        </div>
      </div>
    </div>

    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-[10px] uppercase tracking-[0.32em] flex items-center gap-2">
      <span className="w-10 h-px bg-white/40" />
      Découvrir
      <span className="w-10 h-px bg-white/40" />
    </div>
  </section>
);

const Founder = () => (
  <section className="py-24 md:py-32 bg-white" data-testid="home-founder">
    <div className="container-rozier grid grid-cols-12 gap-10 md:gap-16 items-center">
      <Reveal className="col-span-12 md:col-span-5">
        <div className="relative aspect-[3/4] overflow-hidden">
          <img
            src={FOUNDER_IMAGE}
            alt="Laurie-Line Rozier, fondatrice de Rozier Concept"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute -bottom-3 -right-3 w-32 h-32 border border-gold pointer-events-none" />
        </div>
      </Reveal>

      <div className="col-span-12 md:col-span-7">
        <Reveal>
          <p className="overline mb-6" data-testid="founder-overline">
            La fondatrice
          </p>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-charcoal leading-[1.05] tracking-tight font-light break-words">
            Laurie-Line <span className="italic gold-text">Rozier</span>
          </h2>
        </Reveal>
        <Reveal delay={200}>
          <p className="mt-4 font-serif italic text-graphite text-xl md:text-2xl max-w-2xl leading-snug">
            Artiste paysagiste feng shui, géobiologue et bâtisseuse en écoconstruction.
          </p>
        </Reveal>
        <Reveal delay={300}>
          <p className="mt-8 text-graphite text-base md:text-[17px] leading-[1.85] max-w-xl">
            Passionnée depuis toujours par le bâti, le paysage et l'équilibre des lieux,
            j'ai développé une approche sensible, réfléchie et cohérente de l'habitat et
            de son environnement. La Méthode Rozier s'appuie sur des connaissances
            intenses, des formations et certifications issues de différents corps de
            métiers dont la géobiologie appliquée et le Feng Shui structurel.
          </p>
        </Reveal>
        <Reveal delay={400}>
          <div className="mt-10 flex items-center gap-6">
            <Link
              to="/la-methode-rozier"
              className="inline-flex items-center gap-3 text-charcoal hover:text-gold transition-colors group"
              data-testid="founder-cta-methode"
            >
              <span className="overline">Découvrir la méthode</span>
              <ArrowUpRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const PullQuote = () => (
  <section className="py-24 md:py-32 bg-ivory" data-testid="home-quote">
    <div className="container-rozier">
      <Reveal className="max-w-4xl mx-auto text-center">
        <p className="overline mb-8">Notre philosophie</p>
        <p className="font-serif italic text-charcoal text-3xl md:text-5xl lg:text-6xl leading-[1.15] tracking-tight font-light">
          « Chez Rozier Concept, nous concevons des espaces en travaillant
          <span className="gold-text"> avec la nature</span>, et non contre elle. »
        </p>
        <div className="hairline w-40 mx-auto mt-12" />
        <p className="mt-8 text-graphite max-w-2xl mx-auto leading-relaxed">
          Chaque personne et chaque lieu est unique. Chaque espace possède une identité.
          Notre travail consiste à comprendre ces éléments afin de créer des lieux
          durables, esthétiques et profondément agréables à vivre.
        </p>
      </Reveal>
    </div>
  </section>
);

const Metiers = () => (
  <section id="metiers" className="py-24 md:py-32 bg-white" data-testid="home-metiers">
    <div className="container-rozier">
      <div className="grid grid-cols-12 gap-8 mb-16">
        <Reveal className="col-span-12 md:col-span-6">
          <p className="overline mb-5">Nos trois cœurs de métier</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-charcoal leading-[1.05] tracking-tight font-light break-words">
            Un projet pensé <span className="italic gold-text">dans sa globalité</span>.
          </h2>
        </Reveal>
        <Reveal className="col-span-12 md:col-span-5 md:col-start-8 flex items-end">
          <p className="text-graphite text-base md:text-lg leading-relaxed">
            Trois métiers complémentaires, une seule lecture du lieu. De la conception
            paysagère à la rénovation énergétique, chaque étape s'inscrit dans la
            cohérence de la Méthode Rozier.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {NAV_SECTIONS.map((section, index) => (
          <Reveal key={section.key} delay={index * 120}>
            <Link
              to={section.path}
              className="group relative block h-[460px] md:h-[560px] overflow-hidden bg-charcoal"
              data-testid={`metier-card-${section.key}`}
            >
              <img
                src={section.image}
                alt={section.label}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/10" />
              <div className="absolute inset-0 p-8 md:p-10 flex flex-col justify-end">
                <p className="text-white/70 uppercase tracking-[0.32em] text-[10px] mb-3">
                  0{index + 1}
                </p>
                <h3 className="font-serif text-white text-3xl md:text-4xl leading-tight">
                  {section.label}
                </h3>
                <p className="mt-3 text-white/80 text-sm leading-relaxed max-w-xs">
                  {section.intro}
                </p>
                <div className="mt-6 inline-flex items-center gap-3 text-white">
                  <span className="text-[11px] uppercase tracking-[0.28em] group-hover:text-gold transition-colors">
                    Explorer
                  </span>
                  <ArrowUpRight
                    size={16}
                    className="transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-gold"
                  />
                </div>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const PrivePro = () => (
  <section className="py-24 md:py-32 bg-charcoal text-white" data-testid="home-clients">
    <div className="container-rozier grid grid-cols-12 gap-10 items-center">
      <Reveal className="col-span-12 md:col-span-6">
        <p className="overline text-white/60 mb-6">Particuliers & professionnels</p>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light break-words">
          Un accompagnement <span className="italic gold-text">sur mesure</span>,
          ancré dans la réalité du terrain.
        </h2>
      </Reveal>
      <Reveal className="col-span-12 md:col-span-5 md:col-start-8" delay={150}>
        <p className="text-white/75 text-base md:text-lg leading-relaxed">
          Construction neuve, rénovation, terrain vierge ou bâti existant depuis des
          années. Pour les particuliers comme pour les professionnels, nous concevons
          des lieux harmonieux, durables et porteurs de prospérité.
        </p>
        <div className="grid grid-cols-2 gap-2 mt-10">
          <div className="border border-white/15 p-6">
            <p className="font-serif text-3xl md:text-4xl gold-text">01</p>
            <p className="overline text-white/60 mt-3">Particuliers</p>
            <p className="text-sm text-white/70 mt-2 leading-relaxed">
              Habitats, jardins, harmonisation des lieux de vie.
            </p>
          </div>
          <div className="border border-white/15 p-6">
            <p className="font-serif text-3xl md:text-4xl gold-text">02</p>
            <p className="overline text-white/60 mt-3">Professionnels</p>
            <p className="text-sm text-white/70 mt-2 leading-relaxed">
              Commerces, bureaux, lieux de prospérité durable.
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="page-home">
      <Hero />
      <Founder />
      <PullQuote />
      <Metiers />
      <PrivePro />
    </div>
  );
}
